import React, { useState } from 'react';
import { Box, Typography, IconButton, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

/**
 * Collapsible section card with the same header treatment as StandardHighlight "Key Highlights".
 */
export default function StandardCollapsibleSection({
  title,
  subtitle,
  icon: Icon = LightbulbIcon,
  action,
  defaultExpanded = true,
  children,
  onExpandChange,
  contentSx,
  sx,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    onExpandChange?.(next);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        mb: 2,
        overflow: 'hidden',
        border: '1px solid #eaeaea',
        width: '100%',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          background: 'linear-gradient(to bottom, #4361ee, #3f37c9)',
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 0.75,
          borderBottom: expanded ? '1px solid #f0f0f0' : 'none',
          background: 'linear-gradient(90deg, rgba(242,246,255,0.5) 0%, rgba(255,255,255,0) 100%)',
          justifyContent: 'space-between',
          cursor: 'pointer',
          '&:hover': {
            background: 'linear-gradient(90deg, rgba(242,246,255,0.7) 0%, rgba(255,255,255,0.1) 100%)',
          },
        }}
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 24,
              height: 24,
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #4361ee 0%, #3f37c9 100%)',
              boxShadow: '0 2px 4px rgba(67, 97, 238, 0.2)',
              mr: 1.5,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ color: '#fff', fontSize: 16 }} />
          </Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
            {title}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
            sx={{
              ml: 1,
              color: '#666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                color: '#333',
              },
              padding: '2px',
            }}
            aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
        {action && (
          <Box onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} sx={{ flexShrink: 0, ml: 1 }}>
            {action}
          </Box>
        )}
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2.5, py: 2, ...contentSx }}>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {subtitle}
            </Typography>
          )}
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
